import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  Loader2,
  Copy,
  BookMarked,
  RefreshCw,
  Link2,
  Bookmark,
  BookmarkCheck
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudyView = () => {
  const { volume, book, chapter } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [customText, setCustomText] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [crossRefs, setCrossRefs] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [bookName, setBookName] = useState('');
  const [bookmarkedVerses, setBookmarkedVerses] = useState(new Set());
  const [bookmarkingVerse, setBookmarkingVerse] = useState(null);

  const chapterNum = parseInt(chapter);

  const volumeNames = {
    biblia: 'Biblia',
    libro_de_mormon: 'Libro de Mormón',
    doctrina_y_convenios: 'Doctrina y Convenios',
    perla_de_gran_precio: 'Perla de Gran Precio'
  };

  // Fetch verses from API
  const fetchVerses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/verses/${book}/${chapterNum}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerses(data.verses);
        setBookName(data.book_name);
      } else {
        // Fallback to placeholder
        setVerses(Array.from({ length: 10 }, (_, i) => ({
          verse: i + 1,
          text: `Versículo ${i + 1} - El texto completo estará disponible pronto.`
        })));
        setBookName(book);
      }
    } catch (error) {
      console.error('Error fetching verses:', error);
      setVerses([]);
    } finally {
      setLoading(false);
    }
  }, [book, chapterNum, token]);

  // Fetch bookmarked verses for this chapter
  const fetchBookmarks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookmarks?book=${book}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const bookmarkedInChapter = new Set(
          data
            .filter(b => b.chapter === chapterNum)
            .map(b => b.verse)
        );
        setBookmarkedVerses(bookmarkedInChapter);
      }
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  }, [book, chapterNum, token]);

  // Toggle bookmark for a verse
  const toggleBookmark = async (verse) => {
    setBookmarkingVerse(verse.verse);
    
    const isBookmarked = bookmarkedVerses.has(verse.verse);
    
    try {
      if (isBookmarked) {
        // Remove bookmark
        const response = await fetch(
          `${API_URL}/api/bookmarks/verse/${book}/${chapterNum}/${verse.verse}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (response.ok) {
          setBookmarkedVerses(prev => {
            const newSet = new Set(prev);
            newSet.delete(verse.verse);
            return newSet;
          });
          toast.success('Marcador eliminado');
        }
      } else {
        // Add bookmark
        const response = await fetch(`${API_URL}/api/bookmarks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            book: book,
            book_name: bookName || book,
            volume: volume,
            chapter: chapterNum,
            verse: verse.verse,
            verse_text: verse.text,
            color: 'gold'
          })
        });
        if (response.ok) {
          setBookmarkedVerses(prev => new Set([...prev, verse.verse]));
          toast.success('Versículo marcado');
        }
      }
    } catch (error) {
      toast.error('Error al actualizar marcador');
    } finally {
      setBookmarkingVerse(null);
    }
  };

  // Fetch cross-references for selected verse
  const fetchCrossReferences = useCallback(async (verseNum) => {
    setLoadingRefs(true);
    setCrossRefs([]);
    try {
      const response = await fetch(
        `${API_URL}/api/cross-references/for-verse/${book}/${chapterNum}/${verseNum}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCrossRefs(data.related_themes || []);
      }
    } catch (error) {
      console.error('Error fetching cross-refs:', error);
    } finally {
      setLoadingRefs(false);
    }
  }, [book, chapterNum, token]);

  useEffect(() => {
    fetchVerses();
    fetchBookmarks();
  }, [fetchVerses, fetchBookmarks]);

  useEffect(() => {
    if (selectedVerse) {
      fetchCrossReferences(selectedVerse.verse);
    }
  }, [selectedVerse, fetchCrossReferences]);

  const analyzeVerse = async (verse) => {
    setSelectedVerse(verse);
    setAnalyzing(true);
    setAnalysis(null);

    const textToAnalyze = customText || verse.text;

    try {
      const response = await fetch(`${API_URL}/api/ai/analyze-verse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          book: bookName || book,
          chapter: chapterNum,
          verse: verse.verse,
          verse_text: textToAnalyze
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data.response);
      } else {
        toast.error('Error al analizar el versículo');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setAnalyzing(false);
    }
  };

  const copyToClipboard = () => {
    if (analysis) {
      navigator.clipboard.writeText(analysis);
      toast.success('Copiado al portapapeles');
    }
  };

  const saveAsNote = async () => {
    if (!noteTitle.trim()) {
      toast.error('Ingresa un título para la nota');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: noteTitle,
          content: analysis,
          scripture_ref: `${bookName || book} ${chapter}:${selectedVerse?.verse}`,
          tags: ['estudio', volume]
        })
      });

      if (response.ok) {
        toast.success('Nota guardada exitosamente');
        setSaveDialogOpen(false);
        setNoteTitle('');
      } else {
        toast.error('Error al guardar la nota');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="study-view">
        {/* Navigation */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/scriptures')}
              className="mb-2"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Volver a Escrituras
            </Button>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">
              {bookName || book} {chapter}
            </h1>
            <p className="text-muted-foreground text-sm">
              {volumeNames[volume]}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={chapterNum <= 1}
              onClick={() => navigate(`/study/${volume}/${book}/${chapterNum - 1}`)}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/study/${volume}/${book}/${chapterNum + 1}`)}
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Scripture Text */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <BookOpen className="h-5 w-5 text-primary" />
                Texto de las Escrituras
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-4">
                  {verses.map((verse) => (
                    <div
                      key={verse.verse}
                      className={`p-3 rounded-lg transition-colors group relative ${
                        selectedVerse?.verse === verse.verse
                          ? 'bg-primary/10 border border-primary/30'
                          : 'hover:bg-muted'
                      } ${bookmarkedVerses.has(verse.verse) ? 'border-l-4 border-l-yellow-500' : ''}`}
                      data-testid={`verse-${verse.verse}`}
                    >
                      <div className="flex items-start gap-2">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            setSelectedVerse(verse);
                            setCustomText('');
                          }}
                        >
                          <span className="verse-number">{verse.verse}</span>
                          <span className="scripture-text">{verse.text}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                            bookmarkedVerses.has(verse.verse) ? 'opacity-100 text-yellow-500' : ''
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBookmark(verse);
                          }}
                          disabled={bookmarkingVerse === verse.verse}
                          data-testid={`bookmark-verse-${verse.verse}`}
                        >
                          {bookmarkingVerse === verse.verse ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : bookmarkedVerses.has(verse.verse) ? (
                            <BookmarkCheck className="h-4 w-4" />
                          ) : (
                            <Bookmark className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Analysis Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <Sparkles className="h-5 w-5 text-primary" />
                Análisis con IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedVerse && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    Versículo {selectedVerse.verse} seleccionado
                  </p>
                  <p className="scripture-text text-sm">{selectedVerse.text}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Texto personalizado (opcional)</Label>
                <Textarea
                  placeholder="Escribe aquí si deseas analizar un texto diferente..."
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  className="min-h-[100px]"
                  data-testid="custom-text-input"
                />
              </div>

              <Button
                onClick={() => analyzeVerse(selectedVerse || { verse: 1, text: customText })}
                disabled={(!selectedVerse && !customText) || analyzing}
                className="w-full"
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Analizar Versículo
                  </>
                )}
              </Button>

              {analysis && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      <Copy className="h-4 w-4 mr-1" />
                      Copiar
                    </Button>
                    <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <BookMarked className="h-4 w-4 mr-1" />
                          Guardar Nota
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Guardar como Nota</DialogTitle>
                          <DialogDescription>
                            Guarda este análisis en tu repositorio personal
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Título de la nota</Label>
                            <Input
                              value={noteTitle}
                              onChange={(e) => setNoteTitle(e.target.value)}
                              placeholder="Ej: Reflexión sobre Génesis 1:1"
                              data-testid="note-title-input"
                            />
                          </div>
                          <Button
                            onClick={saveAsNote}
                            disabled={saving}
                            className="w-full"
                            data-testid="save-note-btn"
                          >
                            {saving ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <BookMarked className="mr-2 h-4 w-4" />
                            )}
                            Guardar Nota
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => analyzeVerse(selectedVerse)}
                      disabled={analyzing}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="prose prose-sm dark:prose-invert max-w-none markdown-content">
                      {analysis.split('\n').map((paragraph, i) => (
                        <p key={`analysis-p-${i}`}>{paragraph}</p>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Cross References Section */}
              {selectedVerse && crossRefs.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
                    <Link2 className="h-4 w-4 text-primary" />
                    Referencias Cruzadas
                  </h3>
                  {loadingRefs ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Buscando referencias...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {crossRefs.map((theme) => (
                        <div key={theme.id} className="space-y-2">
                          <Badge variant="secondary" className="mb-1">
                            {theme.name}
                          </Badge>
                          <div className="flex flex-wrap gap-1">
                            {theme.references?.slice(0, 4).map((ref) => (
                              <Link
                                key={ref.ref}
                                to={`/study/${
                                  ref.book.includes('nefi') || ref.book.includes('alma') || ref.book.includes('moroni') || ref.book.includes('mosiah') || ref.book.includes('eter') || ref.book.includes('helaman') || ref.book.includes('mormon') || ref.book.includes('jacob') || ref.book === 'enos' || ref.book === 'jarom' || ref.book === 'omni'
                                    ? 'libro_de_mormon'
                                    : ref.book === 'dyc'
                                    ? 'doctrina_y_convenios'
                                    : ref.book.includes('moises') || ref.book.includes('abraham') || ref.book.includes('jose_smith')
                                    ? 'perla_de_gran_precio'
                                    : 'biblia'
                                }/${ref.book}/${ref.chapter}`}
                              >
                                <Badge
                                  variant="outline"
                                  className="cursor-pointer hover:bg-primary/10 transition-colors"
                                >
                                  {ref.ref}
                                </Badge>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default StudyView;
